<script setup>
import gsap from '~/vendors/gsap'
import { useIsMobile } from '~/composables/useIsMobile'

const props = defineProps({
  layout: {
    type: String,
    default: null,
  },
  eyebrow: {
    type: String,
    default: null,
  },
  icon: {
    type: String,
    default: null,
  },
  title: {
    type: String,
    required: true,
  },
  slug: {
    type: String,
    default: null,
  },
  videoGtmClass: {
    type: String,
    default: null,
  },
  audioGtmClass: {
    type: String,
    default: null,
  },
  thumbnail: {
    type: String,
    default: null,
  },
  video: {
    type: Object,
    default: null,
  },
  audio: {
    type: Object,
    default: null,
  },
  copy: {
    type: [Object, String],
    default: null,
  },
  modal: {
    type: Boolean,
    default: false,
  },
  noClose: {
    type: Boolean,
    default: false,
  },
  hotspot: {
    type: Boolean,
    default: false,
  },
  useAction: {
    type: Boolean,
    default: false,
  },

  // simple slide up animation
  animate: {
    type: Boolean,
    default: false,
  },

  // more complex entrance animation
  unfold: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['close', 'click'])

const container = ref(null)
const videoEl = ref(null)
const videoSrc = computed(() => {
  return props.video?.[0]?.videoPreviewURLs?.[0]
})
const copyIsRichText = computed(
  () => props.copy && typeof props.copy === 'object'
)

/**
 * Mobile
 */
const isMobile = useIsMobile()

/**
 * Slide animation
 */
let timeline
function initAnimation() {
  timeline = gsap
    .timeline({
      scrollTrigger: {
        trigger: container.value,
        start: 'bottom bottom',
      },
    })
    .fromTo(
      container.value.querySelector('.card-content__inner'),
      {
        y: '100%',
      },
      {
        y: '0%',
        duration: 2,
        ease: 'power4.out',
      }
    )
}

function initUnfoldAnimation() {
  timeline = gsap
    .timeline()
    .call(() => {
      container.value.classList.add('card-content--no-bg')
    })
    .from(container.value.querySelector('.card-content__thumbnail'), {
      scale: 0,
      duration: 0.8,
      ease: 'power2.out',
    })
    .addLabel('slide')
    .call(() => {
      container.value.classList.remove('card-content--no-bg')
    })
    .from(
      container.value,
      {
        x: '-=140',
        duration: 0.75,
        ease: 'power2.out',
      },
      'slide'
    )
    .from(
      container.value,
      {
        width: 120,
        duration: 0.75,
        ease: 'power2.out',
      },
      'slide+=0.001'
    )
}

// Lifecycle
onMounted(() => {
  if (props.animate) {
    initAnimation()
  }
  if (props.unfold) {
    initUnfoldAnimation()
  }
})

onBeforeUnmount(() => {
  timeline?.kill()
})

/**
 * Eyebrow
 */
const showEyebrow = computed(
  () => !!props.eyebrow && props.layout !== 'video-preview'
)
</script>

<template>
  <div
    ref="container"
    class="card-content"
    :class="{
      [`card-content--${layout}`]: !!layout,
      'card-content--x-small-video': layout === 'x-small' && !!video,
      'card-content--modal': modal,
      'card-content--hotspot': hotspot,
      'card-content--animate': animate,
      'card-content--unfold': unfold,
      'card-content--has-action': useAction,
    }"
  >
    <ButtonIcon
      v-if="hotspot && !modal"
      class="card-content__close-button"
      icon="plus"
      isOpen
      animateOnMount
      @click="emit('close')"
    >
      <span class="u-visually-hidden">{{
        $t('hotspotButton.viewHotspot')
      }}</span>
    </ButtonIcon>

    <div class="card-content__inner">
      <button
        v-if="useAction"
        class="card-content__action"
        :class="videoGtmClass"
        @click="emit('click')"
      />

      <template v-if="layout === 'video-preview'">
        <ButtonIcon
          v-if="!modal"
          class="card-content__close-button card-content__close-button--video"
          icon="plus"
          is-open
          @click="emit('close')"
        >
          <span class="u-visually-hidden">{{ $t('contentCard.close') }}</span>
        </ButtonIcon>

        <VideoPlayer
          class="card-content__video"
          ref="videoEl"
          autoplay
          :src="videoSrc"
          :title="title"
          :poster="thumbnail"
        />
      </template>
      <div v-else class="card-content__thumbnail-container">
        <img :src="thumbnail" class="card-content__thumbnail" alt="" />

        <template v-if="isMobile">
          <template v-if="layout === 'audio-player'">
            <div class="card-content__player-mobile-container">
              <AudioPlayer
                class="card-content__audio-player-mobile"
                :audio-gtm-class="audioGtmClass"
                :audio="audio"
              />
              <PulseBeat class="card-content__player-pulse" />
            </div>
          </template>

          <template
            v-if="layout === 'livestream' || (layout === 'x-small' && !!video)"
          >
            <div class="card-content__player-mobile-container">
              <button
                class="card-content__mobile-video-button"
                @click="emit('click')"
              >
                <Icon name="play" />
              </button>
              <PulseBeat class="card-content__player-pulse" />
            </div>
          </template>
        </template>
      </div>

      <div class="card-content__content">
        <div class="card-content__content-inner">
          <div v-if="showEyebrow" class="card-content__eyebrow">
            <span
              class="card-content__eyebrow-icon"
              :class="{ [`card-content__eyebrow-icon--${icon}`]: !!icon }"
            >
              <Icon :name="icon" />
              <PulseBeat ref="pulse" class="card-content__eyebrow-pulse" />
            </span>
            <span class="card-content__eyebrow-text">{{ eyebrow }}</span>
          </div>

          <div class="card-content__title">
            <PulseBeat
              v-if="!showEyebrow"
              class="card-content__title-pulse"
              :pos="{ x: 35, y: 40 }"
            />
            <p class="text__body" v-html="$preventOrphans(title)" />
          </div>

          <template v-if="copy">
            <ContentfulRichText
              v-if="copyIsRichText"
              :json="copy.json"
              size="small"
            />
            <p v-else class="text__copy-small">{{ copy }}</p>
          </template>

          <AudioPlayer
            v-if="layout === 'audio-player' && !isMobile"
            :audio="audio"
            :audio-gtm-class="audioGtmClass"
          />
        </div>
      </div>
    </div>
    <slot name="bottom" />
  </div>
</template>

<style lang="scss">
@use 'sass:math';

// Vendors
@import 'styles/vendors/sass-mq/mq';

// Utils
@import 'styles/utils/timing-functions';
@import 'styles/utils/variables';
@import 'styles/utils/functions';

.card-content {
  text-align: left;
  position: relative;
  overflow: hidden;

  &__close-button {
    width: 44px;
    height: 44px;
    position: absolute;
    top: 20px;
    right: 28px;
    z-index: 25;

    &--video {
      width: 32px;
      height: 32px;
      right: 17px;
    }

    .card-content--hotspot & {
      --pos: 16px;
      --size: 42px;

      width: var(--size);
      height: var(--size);
      right: var(--pos);
      top: var(--pos);
    }
  }

  &__close-button .icon {
    color: $brand-white;
    width: 14px;
    height: 14px;
  }

  &__content {
    position: relative;
    height: 100%;
    width: 100%;
  }

  &__eyebrow {
    color: $brand-magenta;
    font-weight: $font-weight-4G;
    font-size: 10px;
    line-height: math.div(15, 10);
    letter-spacing: 0.27em;
    text-transform: uppercase;
    display: flex;
    align-items: center;
  }

  &__eyebrow-icon {
    height: 14px;
    width: 14px;
    margin-right: 5px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__title {
    color: $brand-white;

    .text__copy-small {
      color: $brand-white;
    }
  }

  &__thumbnail-container {
    position: relative;
    overflow: hidden;
    flex-shrink: 0;

    .card-content--hotspot & {
      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: 21;
        background: linear-gradient(
          120.57deg,
          #1e013c 1.24%,
          rgba(30, 1, 60, 0) 44.01%
        );
        transform: matrix(-1, 0, 0, 1, 0, 0);
        pointer-events: none;
      }
    }
  }

  &__thumbnail {
    display: block;
    width: 120px;
    height: 100%;
    object-fit: cover;
    object-position: center;
    // transition: transform 0.3s $ease-in-out-quad;

    @include mq($from: 'l') using ($from) {
      .card-content--x-small:hover & .card-content--small:hover & {
        transform: scale(1.15);
      }
    }
  }

  &__audio-player-mobile {
    margin: auto;
    width: auto;
  }

  &__video {
    width: 100%;
    background: $brand-black;

    &::before {
      content: '';
      pointer-events: none;
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;

      background: linear-gradient(
        239.43deg,
        #1e013c 1.24%,
        rgba(30, 1, 60, 0) 44.01%
      );
    }
  }

  &__inner {
    background: $brand-purple;
  }

  &__action {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 10;
  }

  &--unfold {
    overflow: hidden;
    background: $brand-purple;
    .card-content__inner {
      background: transparent;
    }
  }

  &--x-small,
  &--livestream,
  &--small,
  &--audio-player {
    .card-content {
      &__inner {
        display: flex;
        align-items: stretch;
        min-height: 120px;
      }
      &__content {
        font-size: 16px;
        line-height: math.div(25, 16);
        letter-spacing: -0.01em;
        color: #e4e4ed;
        padding: 21px;
        display: flex;
        flex-direction: column;
        justify-content: center;
        overflow: hidden;
      }
      &__title {
        max-width: 220px;
      }
      &__eyebrow {
        margin-bottom: 11px;
      }
    }
  }

  &--image-preview,
  &--video-preview {
    .card-content {
      &__inner {
        width: 314px;
      }
      &__thumbnail {
        width: 100%;
      }
      &__thumbnail,
      &__video {
        object-fit: cover;
        position: relative;
        z-index: 20;
      }
      &__thumbnail {
        height: 210px;
      }
      &__video {
        height: 170px;
      }

      &__content {
        min-height: 170px;
        padding: 30px 30px 48px;
      }

      &__title {
        display: block;
        margin-bottom: 9px;
      }
    }
  }

  &--audio-player,
  &--x-small-video,
  &--livestream {
    .card-content {
      &__eyebrow-text {
        margin-left: 4px;
      }
      &__title {
        display: block;
        overflow: hidden;
        text-overflow: ellipsis;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        margin-bottom: 6px;
      }

      &__content {
        padding: 21px;
      }
    }

    &__content-inner {
      display: flex;
      flex-direction: column;
    }

    @include mq($until: 'l') using ($until) {
      .card-content {
        &__eyebrow-icon {
          display: none;
        }
        &__thumbnail-container {
          position: absolute;
          top: 0;
          left: 0;
          height: 100%;
          width: 126px;
        }
        &__thumbnail {
          height: 100%;
          width: 100%;
          aspect-ratio: auto;
          object-fit: cover;
        }
        &__inner {
          height: 88px;
        }
        &__content {
          padding: 15px 15px 15px 108px;
          background: linear-gradient(
            90deg,
            rgba(28, 18, 41, 0) 10px,
            #1c1229 120px
          );
        }

        &__mobile-video-button {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          background: rgba(#000, 0.2);
          position: absolute;
          top: calc(50% - 24px);
          left: calc(50% - 24px);
          z-index: 10;
          display: flex;

          .icon {
            width: 16px;
            height: 17px;
            margin: auto;
          }
        }

        &__player-mobile-container {
          position: absolute;
          top: 0;
          left: 0;
          width: 108px;
          height: 100%;
          z-index: 2;
          display: flex;
        }
        &__player-mobile {
          position: absolute;
          top: calc(50% - 24px);
          left: calc(50% - 24px);
        }
      }
    }
  }

  &--livestream {
    width: 100%;
    max-width: 315px;

    .card-content__inner {
      min-height: 88px;
    }
  }

  &--no-bg {
    background: transparent;
  }

  &--modal {
    @include mq($until: 'l') using ($until) {
      display: flex;
      flex-direction: column;
      width: 100vw;
      height: var(--real-height, 100vh);
      position: fixed !important;
      top: 0;
      left: 0;
      right: 0;
      z-index: 100;

      .card-content {
        &__inner {
          display: flex;
          flex-direction: column;
          width: 100%;
          height: 0; // Hack
          flex-grow: 1;
          position: relative;

          &::after {
            content: '';
            background: linear-gradient(
              to top,
              $brand-purple 10%,
              rgba($brand-purple, 0) 100%
            );
            display: block;
            width: 100%;
            height: 60px;
            position: absolute;
            bottom: 0;
            left: 0;
          }
        }

        &__thumbnail {
          max-height: 50vh;
        }
      }

      &.card-content--image-preview,
      &.card-content--video-preview {
        .card-content {
          &__thumbnail,
          &__video {
            aspect-ratio: 375 / 387;
            width: 100%;
            height: auto;
            flex-shrink: 0;
          }

          &__content {
            overflow: auto;
            flex-grow: 1;
            min-height: unset;
          }
        }
      }
    }

    .hotspot-nav {
      background: $brand-purple;
      position: relative;
      flex-shrink: 0;
    }
  }

  @include mq($from: 'l') using ($from) {
    &--x-small,
    &--livestream {
      .card-content__inner {
        width: 314px;
      }

      .card-content__content {
        width: 194px;
      }
    }

    &--small,
    &--audio-player {
      .card-content__inner {
        width: 429px;
      }
    }
  }
}
</style>
